import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import Button from "../../../components/Button/Button";
import { useNotification } from "../../../components/Notification/NotificationManager";
import { useNavigate } from "react-router-dom";
import useScrollToTop from "../../../hooks/useScollToTop";
import { getClubsByUser, getClubs } from "../../../services/clubServices";
import { FaMapMarkerAlt, FaPlus, FaUnlink, FaArrowRight } from "react-icons/fa";
import { FaEdit, FaPaperPlane } from "react-icons/fa";
import { sendNotificationToAdmin } from "../../../services/notificationServices";
import HeroImage from "../../../components/Hero/HeroImage";

const ROLES_IN_CLUB = [
  "Dirigeant(e)",
  "Enseignant(e)",
  "Coach",
  "Arbitre",
  "Judoka(te) licencié(e) au club",
];

const MAP_ZOOM = 14;
const CAN_REQUEST_EDIT = [1, 3, 8]; // Admin, Dirigeant, Enseignant-Coach

// Normalise pour recherche (accents, casse, espaces)
const normalizeStr = (s = "") =>
  s
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const normalizeWebsiteUrl = (url = "") => {
  const trimmed = String(url).trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

// --- en haut du fichier, hors de VotreClub ---
function LinkAnotherClubCard({
  allClubs,
  departements,
  selectedDepartement,
  setSelectedDepartement,
  clubSearch,
  setClubSearch,
  selectedClubId,
  setSelectedClubId,
  selectedRoleInClub,
  setSelectedRoleInClub,
  handleLinkToClub,
  linking,
  instanceKey = "default", // <— pour un id unique si plusieurs cartes
}) {
  const visibleClubs = React.useMemo(() => {
    const base = selectedDepartement
      ? allClubs.filter((c) => c.departement_club === selectedDepartement)
      : allClubs;
    const q = normalizeStr(clubSearch);
    return base
      .filter((c) => (q ? normalizeStr(c.nom_club).includes(q) : true))
      .sort((a, b) => a.nom_club.localeCompare(b.nom_club));
  }, [allClubs, selectedDepartement, clubSearch]);

  const formId = `link-form-${instanceKey}`;

  return (
    <div className="w-full max-w-[1280px] mx-auto mt-4 mb-12">
      <div className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-800/80 shadow-sm p-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg md:text-xl font-semibold dark:text-white">
              Lier mon compte à un club
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
              Recherchez par nom ou filtrez par département, puis choisissez
              votre rôle.
            </p>
          </div>
          <div>
            <Button
              text="Nouveau lien"
              variant="outline"
              size="sm"
              icon={FaPlus}
              className="rounded-full"
              onClick={() => {
                document
                  .getElementById(formId)
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            />
          </div>
        </div>

        <form
          id={formId}
          onSubmit={handleLinkToClub}
          className="mt-4 grid gap-4 md:grid-cols-3"
        >
          {/* Département */}
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Département
            </label>
            <select
              value={selectedDepartement}
              onChange={(e) => setSelectedDepartement(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-700/60 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Tous les départements</option>
              {departements.map((dep) => (
                <option key={dep} value={dep}>
                  {dep}
                </option>
              ))}
            </select>
          </div>

          {/* Club */}
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Club
            </label>
            <select
              value={selectedClubId}
              onChange={(e) => setSelectedClubId(e.target.value)}
              required
              className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-700/60 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">
                {visibleClubs.length ? "Choisir un club" : "Aucun club trouvé"}
              </option>
              {visibleClubs.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nom_club}
                </option>
              ))}
            </select>
          </div>

          {/* Rôle */}
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Rôle dans le club
            </label>
            <select
              value={selectedRoleInClub}
              onChange={(e) => setSelectedRoleInClub(e.target.value)}
              required
              className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-700/60 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {ROLES_IN_CLUB.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          {/* Recherche par nom */}
          <div className="md:col-span-3">
            <label className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Rechercher un club (par nom)
            </label>
            <input
              type="text"
              value={clubSearch}
              onChange={(e) => setClubSearch(e.target.value)}
              placeholder="Ex. : Dojo Bordeaux"
              className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-700/60 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="mt-1 text-xs text-slate-500">
              {visibleClubs.length} club{visibleClubs.length > 1 ? "s" : ""}{" "}
              trouvé{visibleClubs.length > 1 ? "s" : ""}.
            </p>
          </div>

          {/* Actions */}
          <div className="md:col-span-3 flex items-center justify-end gap-3">
            <Button
              text={linking ? "Liaison..." : "Valider mon club"}
              type="submit"
              variant="primary"
              size="md"
              loading={linking}
              icon={FaArrowRight}
              disabled={!visibleClubs.length}
            />
          </div>
        </form>
      </div>
    </div>
  );
}

const VotreClub = () => {
  useScrollToTop();
  const { addNotification } = useNotification();
  const notifyRef = useRef(addNotification);
  useEffect(() => {
    notifyRef.current = addNotification; // garde une référence stable
  }, [addNotification]);
  const navigate = useNavigate();

  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const userId = userInfo?.userId;
  const roleId = userInfo?.role_id;

  // --- Données utilisateur / clubs liés
  const [linkedClubs, setLinkedClubs] = useState([]); // clubs auxquels l'utilisateur est lié
  const [activeClubId, setActiveClubId] = useState(null); // club “focus”
  const [loading, setLoading] = useState(true);

  // --- Sélection pour lier un club
  const [allClubs, setAllClubs] = useState([]);
  const [selectedDepartement, setSelectedDepartement] = useState("");
  const [selectedClubId, setSelectedClubId] = useState("");
  const [selectedRoleInClub, setSelectedRoleInClub] = useState(
    ROLES_IN_CLUB[0],
  );
  const [linking, setLinking] = useState(false);
  const [unlinkingId, setUnlinkingId] = useState(null);

  const [clubSearch, setClubSearch] = useState("");

  const loadLinkedClubs = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const list = await getClubsByUser(userId);
      setLinkedClubs(Array.isArray(list) ? list : []);
      setActiveClubId(Array.isArray(list) && list[0]?.id ? list[0].id : null);
    } catch (e) {
      console.error("Erreur chargement clubs utilisateur :", e);
      notifyRef.current?.("Erreur lors du chargement de vos clubs.", "error");
      setLinkedClubs([]);
      setActiveClubId(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Chargements initiaux
  useEffect(() => {
    loadLinkedClubs();
  }, [loadLinkedClubs]);

  // Charger tous les clubs (pour la sélection) si l'utilisateur n'a aucun club
  useEffect(() => {
    if (linkedClubs.length > 0) return;
    (async () => {
      try {
        const list = await getClubs();
        setAllClubs(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("Erreur chargement liste des clubs :", e);
        setAllClubs([]);
      }
    })();
  }, [linkedClubs.length]);

  // Départements (pour le filtre)
  const departements = useMemo(() => {
    const deps = allClubs.map((c) => c.departement_club).filter(Boolean);
    return [...new Set(deps)].sort();
  }, [allClubs]);

  useEffect(() => {
    setSelectedClubId("");
  }, [selectedDepartement]);

  // Helpers
  const getMapEmbedUrl = (club) => {
    const coords = String(club?.coordonnees_gps || "").trim(); // "lat,lon"
    if (coords && /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(coords)) {
      const q = coords.replace(/\s+/g, "");
      return `https://www.google.com/maps?q=${encodeURIComponent(
        q,
      )}&z=${MAP_ZOOM}&output=embed`;
    }
    const query = [club?.adresse_club, club?.nom_club, club?.departement_club]
      .filter(Boolean)
      .join(" ");
    if (!query) return "";
    return `https://www.google.com/maps?q=${encodeURIComponent(
      query,
    )}&z=${MAP_ZOOM}&output=embed`;
  };

  // Handlers
  const handleNavigation = (path) => navigate(path);

  const handleLinkToClub = async (e) => {
    e.preventDefault();
    if (!selectedClubId)
      return addNotification("Sélectionnez un club.", "error");
    if (!selectedRoleInClub)
      return addNotification("Sélectionnez un rôle dans le club.", "error");

    try {
      setLinking(true);
      // POST /api/clubs/:clubId/members   { user_id, role_in_club }
      const { linkUserToClub } = await import("../../../services/clubServices");
      await linkUserToClub(Number(selectedClubId), {
        user_id: userId,
        role_in_club: selectedRoleInClub,
      });

      addNotification("Votre compte a été lié au club avec succès.", "success");
      await loadLinkedClubs();

      // reset formulaire
      setSelectedDepartement("");
      setSelectedClubId("");
      setSelectedRoleInClub(ROLES_IN_CLUB[0]);
    } catch (err) {
      console.error("Erreur liaison au club :", err);
      addNotification("Impossible de lier votre compte à ce club.", "error");
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkFromClub = async (clubId) => {
    if (!clubId) return;
    const ok = window.confirm(
      "Retirer votre lien avec ce club ? Vous pourrez le relier ultérieurement.",
    );
    if (!ok) return;
    try {
      setUnlinkingId(clubId);
      // DELETE /api/clubs/:clubId/members/:userId
      const { unlinkUserFromClub } =
        await import("../../../services/clubServices");
      // On couvre les deux signatures possibles :
      await (unlinkUserFromClub.length === 2
        ? unlinkUserFromClub(clubId, userId)
        : unlinkUserFromClub(clubId, { user_id: userId }));

      addNotification("Lien avec le club supprimé.", "success");
      await loadLinkedClubs();
      if (activeClubId === clubId) {
        const next = (prevList) =>
          prevList && prevList.length ? (prevList[0]?.id ?? null) : null;
        setActiveClubId(next(linkedClubs));
      }
    } catch (err) {
      console.error("Erreur suppression lien club :", err);
      addNotification("Impossible de supprimer le lien avec le club.", "error");
    } finally {
      setUnlinkingId(null);
    }
  };

  // UI
  const HeaderBreadcrumb = () => (
    <div className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600" />
      <div className="relative text-white p-6 max-w-[1280px] mx-auto">
        <p className="text-sm/6 opacity-90 font-satoshi">
          <span
            onClick={() => handleNavigation("/")}
            className="hover:underline cursor-pointer"
          >
            Accueil
          </span>{" "}
          &gt; Votre Compte &gt; Votre Club
        </p>
        <h1 className="text-2xl md:text-3xl font-bold mt-1">
          Votre/Vos Club(s)
        </h1>
      </div>
    </div>
  );

  const ClubChips = () =>
    linkedClubs.length > 0 ? (
      <div className="max-w-[1280px] mx-auto px-4 mt-4">
        <div className="flex flex-wrap gap-2">
          {linkedClubs.map((c) => (
            <Button
              key={c.id}
              text={c.nom_club}
              onClick={() => setActiveClubId(c.id)}
              size="sm"
              variant={activeClubId === c.id ? "primary" : "subtle"}
              className="rounded-full"
              icon={activeClubId === c.id ? FaArrowRight : undefined}
              hoverLabel={`Afficher ${c.nom_club}`}
            />
          ))}
        </div>
      </div>
    ) : null;

  const ActiveClubCard = () => {
    const club = linkedClubs.find((c) => c.id === activeClubId);
    // Hooks AVANT tout return (ordre d’appel garanti)
    const [editOpen, setEditOpen] = useState(false);
    const [editSending, setEditSending] = useState(false);
    const [editText, setEditText] = useState("");

    if (!club) return null;

    const logo = club.logo_url || "/img/no-image.jpg";
    const mapUrl = getMapEmbedUrl(club);
    const websiteUrl = normalizeWebsiteUrl(club.site_club);

    const canRequestEdit = CAN_REQUEST_EDIT.includes(roleId);

    const openEdit = () => {
      // Pré-rempli pour guider l'utilisateur
      const template = `Téléphone: ${club.tel_club || ""}
Email: ${club.email_club || ""}
Site internet: ${club.site_club || ""}
Adresse dojo: ${club.adresse_club || ""}
Coordonnées GPS: ${club.coordonnees_gps || ""}
Logo (URL): ${club.logo_url || ""}
Commentaire libre: `;
      setEditText(template);
      setEditOpen(true);
      // Scroll doux vers le bloc
      setTimeout(() => {
        document
          .getElementById("edit-block")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 30);
    };

    const submitEditRequest = async () => {
      if (!userId) return;
      try {
        setEditSending(true);
        const nom = userInfo?.nom || "";
        const prenom = userInfo?.prenom || "";
        const message =
          `[Demande MAJ Club]\n` +
          `${nom} ${prenom} (#${userId}) demande une modification pour le club "${club.nom_club}" (id: ${club.id}).\n\n` +
          `${editText.trim()}\n\n` +
          `NB: Merci d'effectuer également la mise à jour sur le "contrat club" depuis le site de la fédération.`;

        await sendNotificationToAdmin(message, userId);
        addNotification("Demande envoyée aux administrateurs ✅", "success");
        setEditOpen(false);
      } catch (e) {
        console.error(e);
        addNotification("Erreur lors de l’envoi de la demande.", "error");
      } finally {
        setEditSending(false);
      }
    };

    return (
      <div className="bg-white/90 dark:bg-slate-800/80 ring-1 ring-slate-200/70 dark:ring-slate-700/70 shadow-sm rounded-2xl p-6 w-full max-w-[1280px] mx-auto mt-4">
        <div className="grid gap-6 md:grid-cols-[160px,1fr,auto] items-center">
          <img
            src={logo}
            alt="Logo Club"
            className="w-36 h-36 mx-auto md:mx-0 rounded-2xl shadow-md ring-1 ring-slate-200 dark:ring-slate-700 object-cover bg-white"
          />
          <div>
            <h2 className="text-xl md:text-2xl font-semibold dark:text-white">
              {club.nom_club}
            </h2>
            <div className="mt-1 text-slate-600 dark:text-slate-300 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-medium">Département :</span>
                <span>{club.departement_club || "—"}</span>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm text-slate-700 dark:text-slate-300">
              <div>{club.adresse_club || "—"}</div>
              <div>☎️ {club.tel_club || "—"}</div>
              <div>✉️ {club.email_club || "—"}</div>
              <div>N° club : {club.numero_club || "—"}</div>

              {websiteUrl && (
                <div className="sm:col-span-2">
                  🌐{" "}
                  <a
                    href={websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:underline"
                  >
                    {club.site_club}
                  </a>
                </div>
              )}

              {club.coordonnees_gps && (
                <div className="flex items-center gap-2">
                  <FaMapMarkerAlt /> {club.coordonnees_gps}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 justify-center items-stretch">
            <Button
              text="Se délier de ce club"
              variant="danger"
              size="sm"
              icon={FaUnlink}
              onClick={() => handleUnlinkFromClub(club.id)}
              loading={unlinkingId === club.id}
              hoverLabel="Supprimer le lien avec ce club"
            />

            {canRequestEdit && (
              <Button
                text="Proposer une modification"
                variant="outline"
                size="sm"
                icon={FaEdit}
                onClick={openEdit}
                hoverLabel="Demander une mise à jour des informations"
              />
            )}
          </div>
        </div>

        {/* Bloc “proposer une modification” */}
        {canRequestEdit && (
          <div
            id="edit-block"
            className={[
              "transition-all duration-300 ease-out overflow-hidden",
              editOpen
                ? "mt-5 max-h-[1000px] opacity-100"
                : "max-h-0 opacity-0",
            ].join(" ")}
          >
            <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 bg-slate-50/80 dark:bg-slate-900/40 p-4">
              <p className="text-sm text-slate-700 dark:text-slate-200">
                <span className="font-semibold">Important :</span> pensez à
                mettre à jour en parallèle votre
                <span className="italic"> “contrat club”</span> sur le site de
                la fédération.
              </p>

              <label className="block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mt-3">
                Détaillez les corrections souhaitées (téléphone, email, site
                internet, adresse dojo, coordonnées GPS, logo…)
              </label>
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={8}
                className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-3 py-2"
                placeholder={`Téléphone:\nEmail:\nSite internet:\nAdresse dojo:\nCoordonnées GPS:\nLogo (URL):\nCommentaire libre:`}
              />

              <div className="mt-3 flex items-center justify-end gap-3">
                <Button
                  text="Annuler"
                  variant="subtle"
                  size="sm"
                  onClick={() => setEditOpen(false)}
                />
                <Button
                  text={editSending ? "Envoi…" : "Envoyer la demande"}
                  variant="primary"
                  size="sm"
                  icon={FaPaperPlane}
                  loading={editSending}
                  onClick={submitEditRequest}
                />
              </div>
            </div>
          </div>
        )}

        {mapUrl && (
          <div className="mt-6 rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-700 bg-slate-50 dark:bg-slate-900">
            <iframe
              title="Localisation du club"
              src={mapUrl}
              className="w-full h-[320px] border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        )}
      </div>
    );
  };

  // ----- Render
  return (
    <div className="w-full pt-[70px]">
      {/* HERO UNIFORMISÉ (tu choisis la photo) */}
      <HeroImage
        title="Votre Club"
        subtitle="Sélectionnez votre/vos clubs"
        imageUrl="/img/cover/cover_belt.webp"
      />

      <div className="max-w-[1280px] mx-auto px-4">
        {!userId ? (
          <div className="bg-white dark:bg-gray-400 shadow-lg rounded-xl p-6 mt-4 w-full text-center">
            <p className="text-red-500">
              Impossible de récupérer votre ID utilisateur. Veuillez vous
              reconnecter.
            </p>
            <Button
              text="Se reconnecter"
              onClick={() => navigate("/login")}
              variant="primary"
              size="md"
              className="mt-4"
            />
          </div>
        ) : loading ? (
          <div className="bg-white dark:bg-gray-400 shadow-lg rounded-xl p-6 mt-4 w-full text-center">
            Chargement...
          </div>
        ) : (
          <>
            {/* Sélecteur rapide des clubs liés */}
            <ClubChips />

            {/* Carte du club actif */}
            {activeClubId ? (
              <ActiveClubCard />
            ) : (
              // Si aucun club : formulaire liaison initiale
              <LinkAnotherClubCard
                allClubs={allClubs}
                departements={departements}
                selectedDepartement={selectedDepartement}
                setSelectedDepartement={setSelectedDepartement}
                clubSearch={clubSearch}
                setClubSearch={setClubSearch}
                selectedClubId={selectedClubId}
                setSelectedClubId={setSelectedClubId}
                selectedRoleInClub={selectedRoleInClub}
                setSelectedRoleInClub={setSelectedRoleInClub}
                handleLinkToClub={handleLinkToClub}
                linking={linking}
                instanceKey="main"
              />
            )}

            {/* Même si déjà lié à des clubs, on peut lier un autre club */}
            {linkedClubs.length > 0 && (
              <LinkAnotherClubCard
                allClubs={allClubs}
                departements={departements}
                selectedDepartement={selectedDepartement}
                setSelectedDepartement={setSelectedDepartement}
                clubSearch={clubSearch}
                setClubSearch={setClubSearch}
                selectedClubId={selectedClubId}
                setSelectedClubId={setSelectedClubId}
                selectedRoleInClub={selectedRoleInClub}
                setSelectedRoleInClub={setSelectedRoleInClub}
                handleLinkToClub={handleLinkToClub}
                linking={linking}
                instanceKey="secondary"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VotreClub;
